import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ChatStatus } from "@/shared/domain/chat/ChatRecord";
import { useChat } from "@/shared/domain/chat/hooks/useChat";
import { useSendMessage } from "@/shared/domain/chat/hooks/useSendMessage";
import { useStartChat } from "@/shared/domain/chat/hooks/useStartChat";

import { useAIChat } from "./AIChatContext";
import { ChatByIdView } from "./ChatByIdView";
import { ChatInputBox } from "./ChatInputBox";
import styles from "./ChatSidebar.module.css";
import { NewChatView } from "./NewChatView";

/**
 * Chat component orchestrates all data fetching and mutations.
 * It conditionally renders NewChat vs ChatMessages based on whether chatId exists.
 * ChatInput is rendered as a pure form component with callbacks.
 */
export const Chat: React.FC = () => {
  const orgId = useCurrentOrgId();
  const { currentChatId, setCurrentChatId, context } = useAIChat();

  // Data fetching
  const chatQuery = useChat(currentChatId, {
    orgId,
    loadMessages: true,
  });
  const chat = chatQuery.data;

  // Mutations
  const startChatMutation = useStartChat();
  const sendMessageMutation = useSendMessage();

  // Derived state
  const loading =
    chatQuery.data !== undefined &&
    chatQuery.data?.status !== ChatStatus.USER_TURN;

  // Auto-scroll to bottom when new messages arrive
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (chatQuery.data?.messages) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatQuery.data?.messages]);

  // Mutation handlers
  const handleStartChat = async (message: string) => {
    const chatRecord = await startChatMutation.mutateAsync({
      message,
      orgId,
      context,
    });
    setCurrentChatId(chatRecord.chat_id);
    return chatRecord;
  };

  const handleSendMessage = async (message: string) => {
    if (!currentChatId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      chatId: currentChatId,
      message: message,
      orgId,
      context,
    });
  };

  // Callbacks for ChatInput
  const handleSubmit = async (message: string): Promise<void> => {
    if (currentChatId) {
      await handleSendMessage(message);
    } else if (message.trim() && !startChatMutation.isPending) {
      await handleStartChat(message);
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (!startChatMutation.isPending) {
      await handleStartChat(question);
    }
  };

  const handleCancel = () => {
    // Reset mutations to cancel any pending requests
    startChatMutation.reset();
    sendMessageMutation.reset();
  };

  const sending = startChatMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {(startChatMutation.isError || sendMessageMutation.isError) && (
          <Alert
            severity="error"
            onClose={() => {
              startChatMutation.reset();
              sendMessageMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startChatMutation.error?.message ||
              sendMessageMutation.error?.message ||
              "An error occurred"}
          </Alert>
        )}

        {/* Conditional Views */}
        <div className={styles.chatView}>
          {chat ? (
            <ChatByIdView chat={chat} messagesEndRef={messagesEndRef} />
          ) : (
            <NewChatView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <ChatInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
        />
      </div>
    </>
  );
};
